import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AlertDialog from 'components/UI/AlertDialog';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import React, { useEffect } from 'react';
import { useCancelWhatsAppCampaignMutation, useGetWhatsAppCampaignQuery } from 'services/api/adminCampaigns.api';
import { useGetLeadMetricsMutation } from 'services/api/insights.api';
import { NAVY, RADIUS_CARD, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

interface CampaignDetailsProps {
  campaignId: number;
  onBack: () => void;
}

const CampaignDetails: React.FC<CampaignDetailsProps> = ({ campaignId, onBack }) => {
  const { data: campaignData, isLoading, error } = useGetWhatsAppCampaignQuery(campaignId);
  const { businessUnits } = useBusinessUnits();
  const [cancelCampaign] = useCancelWhatsAppCampaignMutation();
  const [cancelDialog, setCancelDialog] = React.useState(false);
  const [getLeadMetrics, { data: leadMetrics, isLoading: isLoadingLeadMetrics }] = useGetLeadMetricsMutation();

  const handleCancelCampaign = async () => {
    try {
      await cancelCampaign(campaignId).unwrap();
      setCancelDialog(false);
    } catch (err) {
      console.error('Error cancelling campaign:', err);
    }
  };

  useEffect(() => {
    if (!campaignData?.WhatsAppCampaign) return;
    const campaign = campaignData.WhatsAppCampaign as {
      name: string;
      businessUnitId: number | null;
      startDate?: string | null;
      createdAt: string;
    };
    const end = new Date().toISOString();
    getLeadMetrics({
      startDate: campaign.createdAt,
      endDate: end,
      businessUnitId: campaign.businessUnitId ?? undefined,
      campaignIds: [campaign.name],
    }).catch((err) => {
      console.error('Error getting lead metrics:', err);
    });
  }, [campaignData, getLeadMetrics]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getStateChip = (state: string) => {
    switch (state) {
      case 'RUNNING':
        return (
          <Chip
            icon={<PlayCircleIcon sx={{ fontSize: '14px !important' }} />}
            label="En Ejecución"
            sx={{
              bgcolor: 'rgba(37, 97, 111, 0.12)',
              color: '#25616F',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: '#25616F' },
            }}
          />
        );
      case 'COMPLETED':
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            label="Completada"
            sx={{
              bgcolor: 'rgba(21, 24, 58, 0.08)',
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: NAVY },
            }}
          />
        );
      case 'CANCELLED':
        return (
          <Chip
            icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
            label="Cancelada"
            sx={{
              bgcolor: 'rgba(176, 0, 32, 0.1)',
              color: '#B00020',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              borderRadius: RADIUS_ITEM,
              '& .MuiChip-icon': { color: '#B00020' },
            }}
          />
        );
      default:
        return <Chip label={state} sx={{ borderRadius: RADIUS_ITEM }} />;
    }
  };

  const getMessageStatusChip = (status: string) => {
    const map: Record<string, { label: string; bg: string; color: string }> = {
      pending: { label: 'Pendiente', bg: 'rgba(176, 110, 0, 0.1)', color: '#b05c00' },
      sent: { label: 'Enviado', bg: 'rgba(37, 97, 111, 0.12)', color: '#25616F' },
      failed: { label: 'Fallido', bg: 'rgba(176, 0, 32, 0.1)', color: '#B00020' },
      cancelled: { label: 'Cancelado', bg: 'rgba(21, 24, 58, 0.07)', color: NAVY },
    };
    const s = map[status] || { label: status, bg: 'rgba(21,24,58,0.07)', color: NAVY };
    return (
      <Chip
        label={s.label}
        size="small"
        sx={{
          bgcolor: s.bg,
          color: s.color,
          fontFamily: 'Jost, sans-serif',
          fontWeight: 600,
          borderRadius: RADIUS_ITEM,
        }}
      />
    );
  };

  const getDateRange = (rawStartDate: string | null | undefined) => {
    const startDay = rawStartDate
      ? new Date(rawStartDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    const endDay = new Date().toISOString().split('T')[0];
    return { startDay, endDay };
  };

  const navigateToDashboardWithCampaign = (
    campaignName: string,
    businessUnitId: number | null,
    rawStartDate: string | null | undefined
  ) => {
    const { startDay, endDay } = getDateRange(rawStartDate);
    const params = new URLSearchParams();
    params.set('tab', 'leads');
    if (businessUnitId) params.set('businessUnitId', String(businessUnitId));
    params.set('campaignName', campaignName);
    params.set('startDate', startDay);
    params.set('endDate', endDay);
    params.set('source', campaignName);
    window.open(`/dashboard?${params.toString()}`, '_blank');
  };

  const navigateToLeadsWithCampaignFilter = (
    campaignName: string,
    businessUnitId: number | null,
    createdAt: string | null | undefined
  ) => {
    // Seed the Leads bar dateRange from the campaign's createdAt (not startDate — that can
    // be future-scheduled and would exclude the freshly uploaded leads). The Leads page
    // strips `createdAt` items from the advanced filter and routes them into the bar's
    // dateRange via its `leads.*` namespaced state, so we get a clean "from creation to now"
    // window that always includes the campaign's leads.
    const filterItems: Array<{ field: string; operator: string; value: string | boolean }> = [
      { field: 'whatsAppCampaign', operator: 'in', value: campaignName },
      { field: 'discarded', operator: 'equals', value: false },
    ];
    if (createdAt) {
      filterItems.push({ field: 'createdAt', operator: 'greaterThan', value: createdAt });
    }
    const params = new URLSearchParams();
    params.set('viewMode', 'table');
    if (businessUnitId) params.set('businessUnitId', String(businessUnitId));
    params.set('filterModel', JSON.stringify({ items: filterItems }));
    if (createdAt) params.set('startDate', new Date(createdAt).toISOString().split('T')[0]);
    window.open(`/leads?${params.toString()}`, '_blank');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (error || !campaignData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography sx={{ color: '#B00020', fontFamily: 'Jost, sans-serif' }}>
          Error al cargar los detalles de la campaña
        </Typography>
      </Box>
    );
  }

  const { WhatsAppCampaign: campaign, messagesByStatus } = campaignData;
  const totalMessages = Object.values(messagesByStatus).reduce((sum, count) => sum + count, 0);

  const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box>
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'Jost, sans-serif',
          color: NAVY,
          opacity: 0.45,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 700,
          display: 'block',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      {typeof children === 'string' ? (
        <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500 }}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );

  const fileName = campaign.uploadedFileName ?? null;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              color: NAVY,
              bgcolor: 'rgba(21, 24, 58, 0.06)',
              borderRadius: RADIUS_COIN,
              px: 2.5,
              '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.11)' },
            }}
          >
            Volver
          </Button>
          <Typography variant="h5" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>
            {campaign.name}
          </Typography>
          {getStateChip(campaign.state)}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {campaign.state === 'RUNNING' && (
            <Button
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialog(true)}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                color: '#B00020',
                bgcolor: 'rgba(176, 0, 32, 0.08)',
                borderRadius: RADIUS_COIN,
                px: 2.5,
                '&:hover': { bgcolor: 'rgba(176, 0, 32, 0.14)' },
              }}
            >
              Cancelar Campaña
            </Button>
          )}
          <Button
            endIcon={<OpenInNewIcon />}
            onClick={() =>
              navigateToLeadsWithCampaignFilter(campaign.name, campaign.businessUnitId, campaign.createdAt)
            }
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              bgcolor: YELLOW,
              color: NAVY,
              borderRadius: RADIUS_COIN,
              px: 2.5,
              boxShadow: SHADOW,
              '&:hover': { bgcolor: '#e6c009' },
            }}
          >
            Ver Leads
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* General info */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, p: 3, boxShadow: SHADOW }}>
            <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 2.5 }}>
              Información General
            </Typography>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Unidad de Negocio">
                  {campaign.businessUnit?.name ??
                    businessUnits?.find((bu) => bu.id === campaign.businessUnitId)?.name ??
                    String(campaign.businessUnitId)}
                </InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Plantilla">{campaign.templateName || '—'}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Fecha de Inicio">{campaign.startDate ? formatDate(campaign.startDate) : '—'}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Horario de Envío">
                  {campaign.startHour !== undefined && campaign.endHour !== undefined
                    ? `${campaign.startHour}:00 — ${campaign.endHour}:00`
                    : 'Sin restricción'}
                </InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Mensajes por Período">{String(campaign.messagesToSendPerPeriod)}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Período (minutos)">{String(campaign.timePeriodInMinutes)}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Fin de Semana">{campaign.sendOnWeekend ? 'Sí' : 'No'}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Creado">{formatDate(campaign.createdAt)}</InfoRow>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <InfoRow label="Última Actualización">{formatDate(campaign.updatedAt)}</InfoRow>
              </Grid>
              {fileName && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <InfoRow label="Archivo Base">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <InsertDriveFileIcon sx={{ fontSize: 14, color: NAVY, opacity: 0.5 }} />
                      <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500 }}>
                        {fileName}
                      </Typography>
                    </Box>
                  </InfoRow>
                </Grid>
              )}
              {campaign.templateComponents?.bodyComponents && campaign.templateComponents.bodyComponents.length > 0 && (
                <Grid size={12}>
                  <InfoRow label="Parámetros de la Plantilla">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {campaign.templateComponents.bodyComponents.map((component, index) => {
                        const label =
                          typeof component === 'string' ? component : `${component.paramName}: ${component.value}`;
                        return (
                          <Chip
                            key={`component-${index + 1}-${label}`}
                            label={label}
                            size="small"
                            sx={{
                              bgcolor: SURFACE_CONTAINER_LOWEST,
                              color: NAVY,
                              fontFamily: 'Jost, sans-serif',
                              borderRadius: RADIUS_ITEM,
                              boxShadow: SHADOW,
                            }}
                          />
                        );
                      })}
                    </Box>
                  </InfoRow>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Message stats */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              p: 3,
              boxShadow: SHADOW,
              height: '100%',
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, mb: 2.5 }}>
              Mensajes
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h3"
                sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, lineHeight: 1 }}
              >
                {totalMessages}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  color: NAVY,
                  opacity: 0.45,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                Total
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(messagesByStatus).map(([status, count]) => (
                <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {getMessageStatusChip(status)}
                  <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 700 }}>
                    {count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* Lead metrics */}
        <Grid size={12}>
          <Box sx={{ bgcolor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, p: 3, boxShadow: SHADOW }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Typography variant="h6" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>
                Métricas de Leads
              </Typography>
              <Button
                endIcon={<OpenInNewIcon />}
                onClick={() =>
                  navigateToDashboardWithCampaign(campaign.name, campaign.businessUnitId, campaign.startDate)
                }
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  color: NAVY,
                  bgcolor: 'rgba(21, 24, 58, 0.07)',
                  borderRadius: RADIUS_COIN,
                  px: 2,
                  '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
                }}
              >
                Ver en Dashboard
              </Button>
            </Box>

            {isLoadingLeadMetrics || !leadMetrics ? (
              <Typography variant="body2" sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.5 }}>
                Cargando métricas...
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {[
                  { label: 'Leads Totales', value: leadMetrics.hardMetrics.leadsCount },
                  { label: 'Contactados', value: leadMetrics.hardMetrics.leadsContacted },
                  { label: 'Pendientes', value: leadMetrics.hardMetrics.pendingLeads },
                  { label: 'Fallidos', value: leadMetrics.hardMetrics.leadsThatFailedToBeContacted },
                  { label: 'Subidos a CRM', value: leadMetrics.hardMetrics.leadsUploadedToCRM },
                  { label: 'Respondieron', value: leadMetrics.hardMetrics.leadsThatAnsweredCount },
                  { label: 'Con Fecha de Visita', value: leadMetrics.hardMetrics.leadsWithVisitDate },
                ]
                  .filter(({ value }) => value !== undefined)
                  .map(({ label, value }) => (
                    <Grid key={label} size={{ xs: 6, sm: 4, md: 3 }}>
                      <Box
                        sx={{
                          bgcolor: SURFACE_CONTAINER_LOWEST,
                          borderRadius: RADIUS_ITEM,
                          p: 2,
                          boxShadow: SHADOW,
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY, lineHeight: 1, mb: 0.5 }}
                        >
                          {value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'Jost, sans-serif',
                            color: NAVY,
                            opacity: 0.45,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>

      <AlertDialog
        openDialog={cancelDialog}
        setOpenDialog={setCancelDialog}
        header="Cancelar Campaña"
        msg={`¿Estás seguro de que quieres cancelar la campaña "${campaign.name}"? Esta acción no se puede deshacer y cancelará todos los mensajes pendientes.`}
        confirmMsg="Cancelar Campaña"
        cancelMsg="Mantener Campaña"
        confirmAction={handleCancelCampaign}
      />
    </Box>
  );
};

export default CampaignDetails;
